import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';

import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import Status from 'components/field/Status';
import TimeInput from 'components/field/TimeInput';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Checkbox from 'components/global/Checkbox';
import RangeSlider from 'components/global/RangeSlider';
import Tag from 'components/global/Tag';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import ChargeAccountModal from 'pages/Topbar/ChargeAccount/ChargeAccountModal';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';
import { PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import * as P from '../PriceCalculator.styled';
import * as S from './BulletDeliveryInstructions.styled';

const Ul = ({ children }) => <ul>{children}</ul>;
const Li = ({ children }) => <li>{children}</li>;

const timeToMinutes = (time) =>
	dayjs.duration({ hours: time?.split(':')[0], minutes: time?.split(':')[1] }).asMinutes();

const minutesToTime = (minutes) => dayjs.duration(minutes, 'minutes').format('HH:mm');

const adjustRangeForEnd = (start, end) => {
	if (timeToMinutes('23:59') === end) {
		return [start, timeToMinutes('24:00')];
	}
	if (end < start) {
		return [start, end + 1440];
	}
	return [start, end];
};

const BulletDeliveryInstructions = ({
	nextStep,
	prevStep,
	priceConfig,
	setAggregatorBulletDelivery,
	orderID,
	isQuickShipment
}) => {
	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const { showError } = useAlert();
	const { getTimeWithTimeZone } = useLocale();
	const credit = useSelector((state) => state.user.remainingAmount);
	const ref = useRef(null);

	const {
		currency,
		dcStartHour,
		dcEndHour,
		maxPrice,
		price,
		pickupLocationStartHour,
		pickupLocationEndHour,
		expectedDeliveryMins
	} = priceConfig ?? {};
	const timeNow = useMemo(() => getTimeWithTimeZone(dayjs()), [getTimeWithTimeZone]);

	const ceilMinutes = (time, threshold = 40) => {
		const parsedTime = dayjs(time, 'HH:mm');
		const minutes = parsedTime.minute();
		let adjustedTime;

		if (minutes < threshold) {
			adjustedTime = parsedTime.add(1, 'hour').startOf('hour');
		} else {
			adjustedTime = parsedTime.add(2, 'hours').startOf('hour');
		}

		return adjustedTime.format('HH:mm');
	};

	const minTime = useMemo(
		() => Math.max(timeToMinutes(dcStartHour), timeToMinutes(ceilMinutes(timeNow, 40))),
		[dcStartHour, timeNow]
	);

	const maxTime = useMemo(() => {
		const end = timeToMinutes(dcEndHour);
		return adjustRangeForEnd(minTime, end)[1];
	}, [dcEndHour, minTime]);

	const defaultTimeRange = useMemo(() => {
		const range = [
			Math.max(
				timeToMinutes(dcStartHour),
				timeToMinutes(pickupLocationStartHour),
				timeToMinutes(ceilMinutes(timeNow, 40))
			),
			Math.min(timeToMinutes(pickupLocationEndHour), timeToMinutes(dcEndHour))
		];
		return adjustRangeForEnd(range[0], range[1]);
	}, [dcStartHour, pickupLocationStartHour, pickupLocationEndHour, dcEndHour, timeNow]);

	const minuteStep = useMemo(() => Math.ceil(expectedDeliveryMins / 60) * 60, [expectedDeliveryMins]); // expectedDeliveryMins should be 60-120-180 bcs we are showing 00 hours slots

	const disableTimeRange = useMemo(
		() => minTime === maxTime || maxTime - minTime < minuteStep,
		[minTime, maxTime, minuteStep]
	);

	const isShowPriceSection = useMemo(() => {
		const { maxPrice, price } = priceConfig;
		return maxPrice !== price;
	}, [priceConfig]);

	const {
		control,
		handleSubmit,
		setValue,
		formState: { errors, isFormLoading, isSubmitting },
		getValues,
		clearErrors,
		reset
	} = useFormContext();

	const loading = isFormLoading || isSubmitting;

	const watchedPriceRange = useWatch({ control, name: 'priceRange', defaultValue: [price, maxPrice] });
	const watchedTimeRange = useWatch({
		control,
		name: 'timeRange',
		defaultValue: defaultTimeRange
	});
	const watchedConfirmation = useWatch({ control, name: 'confirmation', defaultValue: false });
	const watchedTimeSlots = useWatch({ control, name: 'timeSlots', defaultValue: [] });

	useEffect(() => {
		if (watchedTimeRange?.length === 2) {
			const [start, end] = watchedTimeRange;
			const slots = [];
			for (let i = end - minuteStep; i >= start; i -= minuteStep) {
				slots.unshift(
					`${minutesToTime(i)} - ${minutesToTime(dayjs.duration(i, 'minutes').add(minuteStep, 'minutes').asMinutes())}`
				);
			}
			setValue('timeSlots', slots);
		}
	}, [watchedTimeRange]);

	const onReset = () => {
		reset({
			...getValues(),
			priceRange: [price, maxPrice],
			timeRange: defaultTimeRange,
			confirmation: false,
			timeSlots: []
		});
		clearErrors();
	};

	useEffect(() => {
		setValue('timeRange', defaultTimeRange);
		setValue('priceRange', [price, maxPrice]);
		return () => {
			onReset();
		};
	}, []);

	useEffect(() => {
		if (defaultTimeRange[1] - defaultTimeRange[0] < minuteStep) {
			setValue('timeRange', [minTime, maxTime]);
		}
	}, [defaultTimeRange, minuteStep]);

	const onChangeTimeRange = (newRange) => {
		setValue('timeRange', adjustRangeForEnd(newRange[0], newRange[1]));
	};

	useEffect(() => {
		clearErrors('confirmation');
	}, [watchedConfirmation]);

	useEffect(() => {
		clearErrors('timeRange');
	}, [watchedTimeRange]);

	useEffect(() => {
		clearErrors('priceRange');
	}, [watchedPriceRange]);

	const bulletCutOff = useMemo(
		() => watchedTimeSlots[watchedTimeSlots.length - 1]?.split(' - ')[0],
		[watchedTimeSlots]
	);

	const instructions = useMemo(
		() => [
			{
				title: 'confirmationSMSTitle',
				description: 'confirmationSMSDescription'
			},
			{
				title: 'confirmationProcessTitle',
				description: 'confirmationProcessDescription'
			},
			{
				title: 'chargesTitle',
				description: 'chargesDescription'
			},
			{
				title: 'cancellingTitle',
				description: 'cancellingDescription'
			}
		],
		[]
	);

	const onCheckRemainingAmount = async () => {
		const price = watchedPriceRange[1];
		if (credit < price) {
			await NiceModal.show(
				ChargeAccountModal,
				isQuickShipment && { defaultSelectedAmount: Math.ceil(price - credit) }
			).then(async () => {
				const { remainingAmount } = await fetchRemainingAmount();
				if (remainingAmount >= price) {
					await onSendSMS();
				} else {
					showError({
						header: 'priceCalculator.notEnoughCreditHeader',
						message: 'priceCalculator.notEnoughCreditMessage'
					});
				}
			});
		} else {
			await onSendSMS();
		}
	};

	const onSendSMS = async () => {
		if (Object.keys(errors).length > 0) {
			return;
		}
		const formattedRanges = watchedTimeSlots.map((range) => {
			const [start, end] = range.split(' - ');
			return {
				start,
				end
			};
		});

		setValue('bulletCutOffTime', bulletCutOff);

		const priceConfig = getValues('priceConfig');
		const priceRange = getValues('priceRange');
		const timeSlotStartHour = minutesToTime(watchedTimeRange[0]);
		const timeSlotEndHour = minutesToTime(watchedTimeRange[1]);

		const data = {
			...priceConfig,
			priceRange: priceRange ?? [price, maxPrice],
			timeSlots: formattedRanges,
			otoId: orderID,
			timeSlotStartHour,
			timeSlotEndHour
		};

		try {
			const response = await axios.post(PRICE_CONFIGURATIONS.BULLET_CONFIRMATION, data);
			if (response.status === 200) {
				nextStep();
			}
		} catch (error) {
			AppLogger.error(error);
			showError({
				message: error?.response?.data?.code
			});
		}
	};

	const scrollToTop = (ref) => {
		ref?.current?.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth'
		});
		ref?.current?.focus();
	};

	return (
		<P.ShippingDocumentsAndPickupOptionsCard fullWidth rounded>
			<S.CardContent ref={ref}>
				<S.Container>
					<S.RangeContainer className={isShowPriceSection ? '' : 'hidden'}>
						<Typography variant="p2" fontWeight="700">
							setUpShippingPriceRange
						</Typography>
						<Alert
							iconUrl="https://storage.googleapis.com/tryoto-public/public/assets/icons/setUpShippingPriceRange.svg"
							content="priceRangeAlert"
							showIcon
						/>

						<S.Column>
							<FormItem label="allowedMaxPrice" className="range">
								<S.Row>
									<IntegerInput
										unit={currency}
										decimalLimit={2}
										step={1.0}
										disabled
										value={watchedPriceRange[0]}
										showArrows={false}
										setValue={(value) => {
											setValue('priceRange', [value, watchedPriceRange[1]]);
										}}
									/>
									<IntegerInput
										min={price}
										max={maxPrice}
										unit={currency}
										decimalLimit={2}
										step={1.0}
										value={watchedPriceRange[1]}
										showArrows={false}
										errorMessage={errors?.priceRange?.message}
										setValue={(value) => {
											setValue('priceRange', [watchedPriceRange[0], value]);
										}}
									/>
								</S.Row>
							</FormItem>

							<FormItem className="range">
								<RangeSlider
									min={price}
									max={maxPrice}
									step={1}
									disabledMin
									value={watchedPriceRange}
									onChange={(newRange) => {
										setValue('priceRange', newRange);
									}}
									isOverlap
								/>
							</FormItem>
						</S.Column>

						<S.CheckboxContainer isError={errors?.confirmation?.message}>
							<Checkbox
								id="confirmation"
								checked={watchedConfirmation}
								onChange={(e) => setValue('confirmation', e?.target?.checked)}
								error={errors?.confirmation?.message}
							/>
							<Typography fontWeight="500">
								<label htmlFor="confirmation"> {t('understandAndConfirmBullet')}</label>
							</Typography>
						</S.CheckboxContainer>
					</S.RangeContainer>

					<S.Divider className={isShowPriceSection ? '' : 'hidden'} />

					<S.RangeContainer>
						<Typography variant="p2" fontWeight="700">
							availableDeliveryHours
						</Typography>

						<Alert
							iconUrl="https://storage.googleapis.com/tryoto-public/public/assets/icons/availableDeliveryHours.svg"
							content="timeRangeAlert"
							showIcon
						/>

						<S.Column>
							<FormItem label="allowedTime" className="range">
								<S.Row isError={!!errors?.timeRange?.message}>
									<TimeInput
										minTime={minutesToTime(minTime)}
										maxTime={minutesToTime(watchedTimeRange[1] - minuteStep)}
										hourIncrement={minuteStep / 60}
										minuteIncrement={minuteStep}
										editableMinute={false}
										className="time-input"
										value={minutesToTime(watchedTimeRange[0])}
										setValue={(val) => {
											onChangeTimeRange([timeToMinutes(val), watchedTimeRange[1]]);
										}}
										errorMessage={errors?.timeRange?.message || errors?.timeSlots?.message}
										disabled={disableTimeRange}
									/>
									<TimeInput
										minTime={minutesToTime(watchedTimeRange[0] + minuteStep)}
										maxTime={minutesToTime(maxTime)}
										hourIncrement={minuteStep / 60}
										minuteIncrement={minuteStep}
										editableMinute={false}
										className="time-input"
										value={minutesToTime(watchedTimeRange[1])}
										setValue={(val) => {
											onChangeTimeRange([watchedTimeRange[0], timeToMinutes(val)]);
										}}
										errorMessage={errors?.timeRange?.message || errors?.timeSlots?.message}
										disabled={disableTimeRange}
									/>
								</S.Row>
							</FormItem>

							<FormItem className="range">
								<RangeSlider
									min={minTime}
									max={maxTime}
									step={minuteStep}
									value={watchedTimeRange}
									onChange={(newRange) => {
										onChangeTimeRange(newRange);
									}}
									disabledMin={disableTimeRange}
									disabledMax={disableTimeRange}
								/>
							</FormItem>
						</S.Column>
					</S.RangeContainer>

					<S.DeliverySlotWrapper>
						<Typography variant="p3" color="var(--content-3)">
							previewAvailableDeliverySlots
						</Typography>

						<S.TagsContainer>
							{watchedTimeSlots?.map((slot, index) => (
								<Tag variant="disabled" key={index} label={slot} />
							))}
						</S.TagsContainer>
					</S.DeliverySlotWrapper>
					{watchedTimeSlots?.length > 0 && (
						<S.CutoffTimeWrapper>
							<Typography variant="p3">
								<Trans
									i18nKey="cutOffTimeInformation"
									values={{ value: bulletCutOff }}
									components={{ p: <Typography />, blackText: <S.BlackText /> }}
								/>
							</Typography>
						</S.CutoffTimeWrapper>
					)}
				</S.Container>

				<S.Divider />

				<P.InstructionWrapper>
					<Typography variant="p2" fontWeight="700">
						instructions
					</Typography>
					{instructions.map((instruction, index) => (
						<P.InstructionRow key={instruction.title}>
							<Typography variant="p2" fontWeight="500" className="instruction-number">
								{index + 1}
							</Typography>

							<P.InstructionColumn>
								<Typography variant="p2" fontWeight="700">
									{instruction.title}
								</Typography>
								<Typography variant="p3" color="var(--content-3)" noWrap className="instruction-detail">
									<Trans
										i18nKey={instruction.description}
										components={{
											waitingCustomerConfirmation: <Status value="waitingCustomerConfirmation" />,
											confirmedAndScheduled: <Status value="confirmedAndScheduled" />,
											confirmationFailed: <Status value="confirmationFailed" />,
											ul: <Ul />,
											li: <Li />,
											p: <Typography color="var(--content-3)" noWrap />
										}}
									/>
								</Typography>
							</P.InstructionColumn>
						</P.InstructionRow>
					))}
				</P.InstructionWrapper>
			</S.CardContent>
			<P.ShippingDocumentsAndPickupOptionsButtonContainer>
				<Button
					icon={faChevronLeft}
					label="previous"
					variant="outline"
					onClick={() => {
						prevStep();
						setAggregatorBulletDelivery(false);
					}}
					disabled={loading}
				/>
				<Button
					icon={faChevronRight}
					iconPos="right"
					label="sendSmsButtonCTA"
					onClick={handleSubmit(onCheckRemainingAmount, (errors) => {
						scrollToTop(ref);
						AppLogger.log(errors);
					})}
					loading={loading}
					disabled={loading}
				/>
			</P.ShippingDocumentsAndPickupOptionsButtonContainer>
		</P.ShippingDocumentsAndPickupOptionsCard>
	);
};

export default BulletDeliveryInstructions;
